// src/components/admin/PolicyManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { policyAPI } from '../../services/api';

/* ─── field editor for different value types ─────────────────────────────── */
function FieldEditor({ config, value, onChange }) {
  const { value_type, choices, min_value, max_value } = config;

  if (value_type === 'boolean') {
    const checked = value === 'true' || value === true;
    return (
      <label style={S.toggleWrap}>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked ? 'true' : 'false')}
          style={S.checkboxInput}
        />
        <span style={{
          ...S.toggleTrack,
          background: checked ? '#4f46e5' : '#cbd5e1',
        }}>
          <span style={{
            ...S.toggleThumb,
            left: checked ? 18 : 2,
          }} />
        </span>
        <span style={S.toggleLabel}>{checked ? 'Enabled' : 'Disabled'}</span>
      </label>
    );
  }

  if (value_type === 'choice' && Array.isArray(choices)) {
    return (
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={S.select}
      >
        {choices.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    );
  }

  if (value_type === 'integer') {
    return (
      <div style={S.numWrap}>
        <input
          type="number"
          step="1"
          min={min_value ?? undefined}
          max={max_value ?? undefined}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={S.numInput}
        />
        {(min_value !== null || max_value !== null) && (
          <span style={S.numRange}>
            Range: {min_value ?? '—'} – {max_value ?? '—'}
          </span>
        )}
      </div>
    );
  }

  if (value_type === 'decimal') {
    return (
      <div style={S.numWrap}>
        <input
          type="number"
          step="0.01"
          min={min_value ?? undefined}
          max={max_value ?? undefined}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={S.numInput}
        />
        {(min_value !== null || max_value !== null) && (
          <span style={S.numRange}>Range: {min_value ?? '—'} – {max_value ?? '—'}</span>
        )}
      </div>
    );
  }

  // string
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={S.textInput}
      maxLength={500}
    />
  );
}

/* ─── main component ─────────────────────────────────────────────────────── */
export default function PolicyManagement({ category, categoryLabel }) {
  const [configs,  setConfigs]  = useState([]);
  const [dirty,    setDirty]    = useState({});   // { key: newValue }
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const data = await policyAPI.getAll(category);
      const list = Array.isArray(data) ? data : (data.results || []);
      setConfigs(list);
      setDirty({});
    } catch (e) {
      setError('Failed to load policies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key, newValue) => {
    setDirty(d => ({ ...d, [key]: newValue }));
  };

  const getValue = (cfg) =>
    dirty[cfg.key] !== undefined ? dirty[cfg.key] : cfg.value;

  const hasDirty = Object.keys(dirty).length > 0;

  const handleSaveAll = async () => {
    if (!hasDirty) return;
    try {
      setSaving(true); setError(null);
      const updates = Object.entries(dirty).map(([key, value]) => ({ key, value }));
      const res = await policyAPI.bulkUpdate(updates);
      setSuccess(`${res.saved?.length || updates.length} setting(s) saved successfully.`);
      setTimeout(() => setSuccess(''), 4000);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetOne = (key) => {
    setDirty(d => { const n = { ...d }; delete n[key]; return n; });
  };

  if (loading) {
    return (
      <div style={S.loadingWrap}>
        <div style={S.spinner} />
        <span style={{ color: '#64748b', fontSize: 14 }}>Loading {categoryLabel}…</span>
      </div>
    );
  }

  return (
    <div>
      {/* alerts */}
      {error   && <div style={S.alertErr}>{error}<button style={S.alertClose} onClick={() => setError(null)}>×</button></div>}
      {success && <div style={S.alertOk}>✓ {success}<button style={S.alertClose} onClick={() => setSuccess('')}>×</button></div>}

      {/* header */}
      <div style={S.header}>
        <div>
          <span style={S.configCount}>{configs.length} setting{configs.length !== 1 ? 's' : ''}</span>
          {hasDirty && <span style={S.dirtyBadge}>{Object.keys(dirty).length} unsaved change{Object.keys(dirty).length !== 1 ? 's' : ''}</span>}
        </div>
        <div style={S.headerBtns}>
          {hasDirty && (
            <>
              <button style={S.btnCancel} onClick={() => setDirty({})} disabled={saving}>Discard</button>
              <button style={S.btnSave}   onClick={handleSaveAll}      disabled={saving}>
                {saving ? 'Saving…' : `💾 Save ${Object.keys(dirty).length} Change${Object.keys(dirty).length !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
        </div>
      </div>

      {/* settings list */}
      {configs.length === 0 ? (
        <div style={S.empty}>No settings found for this category.</div>
      ) : (
        <div style={S.list}>
          {configs.map(cfg => {
            const isDirty  = dirty[cfg.key] !== undefined;
            const curValue = getValue(cfg);

            return (
              <div key={cfg.key} style={{ ...S.row, ...(isDirty ? S.rowDirty : {}) }}>
                <div style={S.rowLeft}>
                  <div style={S.rowLabel}>
                    {cfg.label}
                    {isDirty && <span style={S.changedDot} title="Unsaved change">●</span>}
                  </div>
                  {cfg.description && (
                    <div style={S.rowDesc}>{cfg.description}</div>
                  )}
                  <div style={S.rowMeta}>
                    <span style={S.keyChip}>{cfg.key}</span>
                    <span style={S.typeChip}>{cfg.value_type}</span>
                    {!cfg.is_active && <span style={S.inactiveChip}>inactive</span>}
                  </div>
                </div>
                <div style={S.rowRight}>
                  <FieldEditor
                    config={cfg}
                    value={curValue}
                    onChange={v => handleChange(cfg.key, v)}
                  />
                  {isDirty && (
                    <div style={S.originalRow}>
                      <span style={S.originalLabel}>Was:</span>
                      <span style={S.originalVal}>{cfg.value}</span>
                      <button style={S.resetBtn} onClick={() => handleResetOne(cfg.key)}>Reset</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* sticky save bar when dirty */}
      {hasDirty && (
        <div style={S.stickyBar}>
          <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>
            You have {Object.keys(dirty).length} unsaved change{Object.keys(dirty).length !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btnCancel} onClick={() => setDirty({})}>Discard All</button>
            <button style={S.btnSave}   onClick={handleSaveAll} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save All Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── styles ─────────────────────────────────────────────────────────────── */
const S = {
  loadingWrap:  { display: 'flex', alignItems: 'center', gap: 12, padding: 48, justifyContent: 'center' },
  spinner: {
    width: 22, height: 22, border: '3px solid #e2e8f0', borderTopColor: '#4f46e5',
    borderRadius: '50%', animation: 'spin .7s linear infinite',
  },
  alertErr: {
    background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
    padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
    position: 'relative',
  },
  alertOk: {
    background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
    padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
    position: 'relative',
  },
  alertClose: {
    position: 'absolute', top: 8, right: 10, background: 'none', border: 'none',
    cursor: 'pointer', fontSize: 16, color: '#94a3b8',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16, flexWrap: 'wrap', gap: 8,
  },
  configCount:  { fontSize: 13, color: '#64748b', fontWeight: 500 },
  dirtyBadge: {
    marginLeft: 10, background: '#fef9c3', color: '#854d0e',
    padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
  },
  headerBtns:   { display: 'flex', gap: 8 },
  btnGhost: {
    padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
    background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#374151',
  },
  btnCancel: {
    padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
    background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#374151',
  },
  btnSave: {
    padding: '7px 18px', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
    color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  empty: { textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 },
  list:  { display: 'flex', flexDirection: 'column', gap: 1 },
  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '16px 18px', background: '#fff', borderRadius: 10,
    border: '1px solid #e5e7eb', gap: 24, flexWrap: 'wrap',
    transition: 'border-color .15s, background .15s',
  },
  rowDirty: { border: '1px solid #a78bfa', background: '#faf5ff' },
  rowLeft:  { flex: '1 1 240px', minWidth: 200 },
  rowRight: { flex: '0 0 260px', display: 'flex', flexDirection: 'column', gap: 6 },
  rowLabel: {
    fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  changedDot:   { color: '#7c3aed', fontSize: 10 },
  rowDesc:      { fontSize: 12, color: '#64748b', lineHeight: 1.5, marginBottom: 6 },
  rowMeta:      { display: 'flex', gap: 6, flexWrap: 'wrap' },
  keyChip:  { background: '#f1f5f9', color: '#475569', padding: '2px 7px', borderRadius: 6, fontSize: 11, fontFamily: 'monospace' },
  typeChip: { background: '#ede9fe', color: '#6d28d9', padding: '2px 7px', borderRadius: 6, fontSize: 11, fontWeight: 600 },
  inactiveChip: { background: '#f3f4f6', color: '#9ca3af', padding: '2px 7px', borderRadius: 6, fontSize: 11 },
  originalRow:  { display: 'flex', alignItems: 'center', gap: 6 },
  originalLabel: { fontSize: 11, color: '#94a3b8' },
  originalVal:  { fontSize: 11, color: '#374151', fontFamily: 'monospace' },
  resetBtn: {
    fontSize: 11, color: '#6d28d9', background: 'none', border: 'none',
    cursor: 'pointer', textDecoration: 'underline', padding: 0,
  },
  stickyBar: {
    position: 'sticky', bottom: 16, marginTop: 20,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: '#fff', border: '1px solid #e2e8f0',
    boxShadow: '0 4px 20px #0002', borderRadius: 12, padding: '12px 18px',
    flexWrap: 'wrap', gap: 10,
  },
  /* field editors */
  toggleWrap: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  checkboxInput: { display: 'none' },
  toggleTrack: {
    width: 38, height: 22, borderRadius: 11, position: 'relative',
    display: 'inline-block', transition: 'background .2s', cursor: 'pointer', flexShrink: 0,
  },
  toggleThumb: {
    position: 'absolute', width: 16, height: 16, borderRadius: '50%',
    background: '#fff', top: 3, transition: 'left .2s',
    boxShadow: '0 1px 3px #0003',
  },
  toggleLabel: { fontSize: 13, color: '#374151', fontWeight: 500 },
  numWrap:  { display: 'flex', flexDirection: 'column', gap: 4 },
  numInput: {
    padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 14, width: '100%', boxSizing: 'border-box', outline: 'none',
    fontFamily: 'monospace',
  },
  numRange: { fontSize: 11, color: '#94a3b8' },
  select: {
    padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 13, width: '100%', boxSizing: 'border-box',
    background: '#fff', cursor: 'pointer',
  },
  textInput: {
    padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none',
  },
};

/* inject keyframes once */
if (typeof document !== 'undefined' && !document.getElementById('pm-spin-kf')) {
  const style = document.createElement('style');
  style.id = 'pm-spin-kf';
  style.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(style);
}